import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { ShieldQuestion, Loader2, Check, Bot, RefreshCw } from "lucide-react";

/** AdminQuestionAuditPage — review AI-flagged answer keys. Human decides; nothing auto-changes. */
const AdminQuestionAuditPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [verdict, setVerdict] = useState("disagree");

  const load = useCallback(async (v) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/question-audit?verdict=${v}`);
      setData(r);
    } catch (e) {
      toast.error("Failed to load audit results");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(verdict); }, [load, verdict]);

  const resolve = async (qid, action) => {
    setBusy((b) => ({ ...b, [qid]: true }));
    try {
      await api.post(`/admin/question-audit/${qid}/resolve`, { action });
      toast.success(action === "accept_llm" ? "Answer key updated" : "Kept current answer");
      setData((d) => ({ ...d, items: d.items.filter((i) => i.question_id !== qid) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    } finally {
      setBusy((b) => ({ ...b, [qid]: false }));
    }
  };

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-question-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldQuestion className="w-6 h-6 text-primary" /> Question audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            An AI examiner answered every question independently and flagged where it disagrees with
            the stored answer key. Review each flag — nothing changes unless you approve it.
          </p>
        </div>
        <button onClick={() => load(verdict)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm hover:bg-secondary" data-testid="audit-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["total","Audited"],["agree","AI agrees"],["disagree","Flagged"],["uncertain","Uncertain"],["reviewed","Reviewed"]].map(([k,label]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-xl font-bold">{counts[k] ?? "—"}</p>
            <p className="text-[11px] text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {["disagree","uncertain","all"].map((v) => (
          <button key={v} onClick={() => setVerdict(v)}
            className={`px-3 py-1.5 rounded-full text-sm border ${verdict===v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
            data-testid={`audit-filter-${v}`}>
            {v}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-2xl">
          Nothing awaiting review in this filter. 🎉
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => {
            const stored = (it.options || []).find((o) => o.id === it.stored_answer_id);
            const llm = (it.options || []).find((o) => o.id === it.llm_answer_id);
            return (
              <div key={it.question_id} className="rounded-2xl border border-border bg-card p-5" data-testid={`audit-item-${it.question_id}`}>
                <p className="font-medium text-sm mb-3">{it.question_text}</p>
                <div className="space-y-1.5 mb-3">
                  {(it.options || []).map((o) => (
                    <div key={o.id} className={`text-sm px-3 py-1.5 rounded-lg border ${
                      o.id === it.stored_answer_id && o.id === it.llm_answer_id ? "border-emerald-400 bg-emerald-50" :
                      o.id === it.stored_answer_id ? "border-blue-300 bg-blue-50" :
                      o.id === it.llm_answer_id ? "border-amber-300 bg-amber-50" : "border-border"}`}>
                      <b className="uppercase mr-1">{o.id})</b> {o.text}
                      {o.id === it.stored_answer_id && <span className="ml-2 text-[11px] text-blue-600 font-medium">current key</span>}
                      {o.id === it.llm_answer_id && o.id !== it.stored_answer_id && <span className="ml-2 text-[11px] text-amber-600 font-medium">AI suggests</span>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mb-3 flex items-start gap-1.5">
                  <Bot className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{it.reason} {typeof it.confidence === "number" && `(confidence ${(it.confidence*100).toFixed(0)}%)`}</span>
                </p>
                <div className="flex gap-2">
                  <button onClick={() => resolve(it.question_id, "keep")} disabled={busy[it.question_id]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-secondary disabled:opacity-50"
                    data-testid={`audit-keep-${it.question_id}`}>
                    <Check className="w-4 h-4" /> Keep current ({(stored?.id || "?").toUpperCase()})
                  </button>
                  {it.verdict === "disagree" && llm && (
                    <button onClick={() => resolve(it.question_id, "accept_llm")} disabled={busy[it.question_id]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm hover:bg-amber-600 disabled:opacity-50"
                      data-testid={`audit-accept-${it.question_id}`}>
                      <Bot className="w-4 h-4" /> Accept AI answer ({(llm?.id || "?").toUpperCase()})
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminQuestionAuditPage;
